import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQueries } from "@tanstack/react-query";
import { format } from "date-fns";
import { enUS, arSA } from "date-fns/locale";
import { Link, useParams, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Loader2, MapPin, Clock, ArrowLeft, ArrowRight, CheckCircle2, Plus, Wallet } from "lucide-react";
import { cn } from "@/lib/utils";
import { apiClient } from "@/lib/api-client";
import { useOrder } from "@/hooks/use-orders";
import { useAddresses } from "@/hooks/use-addresses";
import { useAvailableTimeSlots } from "@/hooks/use-locations";
import { useEditOrder } from "@/hooks/use-orders";
import { useWalletBalance } from "@/hooks/use-wallet";
import { usePublicSettings, DEFAULT_EDIT_CUTOFF_MINUTES } from "@/hooks/use-settings";
import { useTheme } from "@/components/ThemeProvider";
import knetLogo from "@assets/knet-logo.png";
import creditCardLogos from "@assets/credit-card-logos.jpg";
import applePayLogoLight from "../assets/images/apple_pay_light.png";
import applePayLogoDark from "../assets/images/apple_pay_dark.png";

const WHATSAPP_NUMBER = "96566098080";

const arabicDayNames: Record<string, string> = {
  'Sun': 'الأحد', 'Mon': 'الاثنين', 'Tue': 'الثلاثاء', 'Wed': 'الأربعاء',
  'Thu': 'الخميس', 'Fri': 'الجمعة', 'Sat': 'السبت',
};

export default function EditOrder() {
  const { t, i18n } = useTranslation();
  const { data: publicSettings } = usePublicSettings();
  const isRtl = i18n.language === 'ar';
  const params = useParams<{ id: string }>();
  const [, navigate] = useLocation();

  const { data: order, isLoading } = useOrder(params.id || '');
  const { data: addresses } = useAddresses();
  const editMutation = useEditOrder();

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedAddressId, setSelectedAddressId] = useState<string>("");
  const [selectedTimeSlotId, setSelectedTimeSlotId] = useState<string>("");
  const [paymentMethod, setPaymentMethod] = useState<'knet' | 'credit_card' | 'apple_pay' | 'wallet'>('wallet');
  const { theme } = useTheme();
  const { data: walletData } = useWalletBalance();
  const walletBalance = walletData?.balance ?? 0;

  // Default-select the order's current address once the order loads
  useEffect(() => {
    if (order?.addressId && !selectedAddressId) {
      setSelectedAddressId(order.addressId);
    }
  }, [order?.addressId]);

  // Pre-select the current appointment date and time slot
  useEffect(() => {
    if (!order) return;
    // Date
    const dateField = order.appointmentDate || order.appointmentDateTime;
    if (dateField && !selectedDate) {
      setSelectedDate(new Date(dateField));
    }
    // Time slot
    if (order.timeSlotId && !selectedTimeSlotId) {
      setSelectedTimeSlotId(order.timeSlotId);
    }
  }, [order]);

  // All saved addresses — selectable if delivery fee is same or lower than the order's
  const allAddresses = useMemo(() => addresses || [], [addresses]);
  const orderDeliveryFee = order?.deliveryFee ?? 0;

  // Unique district IDs across all addresses (to fetch their fees)
  const uniqueDistrictIds = useMemo(() => {
    const ids = new Set<string>();
    allAddresses.forEach((a: any) => {
      if (a.districtId) ids.add(a.districtId);
    });
    return Array.from(ids);
  }, [allAddresses]);

  // Fetch delivery fee for each unique district
  const feeQueries = useQueries({
    queries: uniqueDistrictIds.map((districtId) => ({
      queryKey: ['/districts', districtId, 'delivery-fee'],
      queryFn: async (): Promise<{ deliveryFee: number }> => {
        const res = await apiClient.get(`/districts/${districtId}/delivery-fee`);
        if (!res.ok) return { deliveryFee: 0 };
        return res.json();
      },
      enabled: !!districtId,
    })),
  });

  // Map: districtId → delivery fee
  const districtFees = useMemo(() => {
    const map: Record<string, number> = {};
    uniqueDistrictIds.forEach((districtId, i) => {
      const data = feeQueries[i]?.data;
      map[districtId] = data?.deliveryFee ?? 0;
    });
    return map;
  }, [uniqueDistrictIds, feeQueries]);

  // Delivery fee for a given address. Higher-fee areas are now ALLOWED — the
  // difference is charged (wallet, else payment gateway) on save.
  const getAddressFee = (addr: any): number => {
    return addr.districtId ? (districtFees[addr.districtId] ?? orderDeliveryFee) : orderDeliveryFee;
  };
  // Extra amount the customer pays if they pick this address.
  const getAddressExtraFee = (addr: any): number => {
    return Math.max(0, getAddressFee(addr) - orderDeliveryFee);
  };
  // The extra fee for the currently-selected address (drives whether the
  // payment-method chooser is shown).
  const selectedAddress = allAddresses.find((a: any) => a.id === selectedAddressId);
  const selectedExtraFee = selectedAddress ? getAddressExtraFee(selectedAddress) : 0;

  // Next 14 days (starting today, same as Booking)
  const dateOptions = useMemo(() => {
    const dates: Date[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = 0; i < 14; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      dates.push(d);
    }
    return dates;
  }, []);

  const getDayName = (date: Date): string => {
    const en = format(date, 'EEE', { locale: enUS });
    return isRtl ? (arabicDayNames[en] || en) : en;
  };

  const formattedDate = selectedDate ? format(selectedDate, "yyyy-MM-dd") : undefined;
  const { data: timeSlots, isLoading: slotsLoading } = useAvailableTimeSlots(formattedDate);

  const handleSave = () => {
    if (!order || (!selectedAddressId && !selectedTimeSlotId)) return;
    const addressId = selectedAddressId || undefined;
    editMutation.mutate(
      {
        id: order.id,
        addressId,
        timeSlotId: selectedTimeSlotId || undefined,
        paymentMethod: selectedAddressId ? paymentMethod : undefined,
      },
      {
        onSuccess: (data) => {
          // If the backend requires a payment (higher-fee area + insufficient
          // wallet), redirect to the gateway. Otherwise the edit is done.
          if (data?.requiresPayment && data.paymentLink) {
            window.location.href = data.paymentLink;
          } else {
            navigate("/orders");
          }
        },
      },
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen pt-24 pb-24 bg-background">
        <div className="container mx-auto px-4 max-w-2xl">
          <Skeleton className="h-8 w-48 mb-6" />
          <Skeleton className="h-32 w-full mb-4" />
          <Skeleton className="h-24 w-full mb-4" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen pt-24 pb-24 bg-background">
        <div className="container mx-auto px-4 max-w-2xl text-center">
          <p className="text-muted-foreground">{t("orders.subtitle")}</p>
          <Link href="/orders">
            <Button className="mt-4">{t("common.previous")}</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Mirrors the server's configurable cutoff — see EditOrderDialog.
  const cutoffMinutes =
    publicSettings?.customerEditCutoffMinutes ?? DEFAULT_EDIT_CUTOFF_MINUTES;
  const appointmentTime = order.appointmentDateTime ? new Date(order.appointmentDateTime) : null;
  const tooLate = appointmentTime
    ? appointmentTime.getTime() < Date.now() + cutoffMinutes * 60 * 1000
    : false;

  return (
    <div className="min-h-screen pt-24 pb-24 bg-background">
      <div className="container mx-auto px-4 max-w-2xl">
        {/* Back button */}
        <Link href="/orders" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
          {isRtl ? <ArrowRight className="h-4 w-4" /> : <ArrowLeft className="h-4 w-4" />}
          {t("orders.title")}
        </Link>

        <h1 className="text-2xl font-bold font-display mb-2">{t("orders.editOrder")}</h1>
        <p className="text-muted-foreground mb-6">{t("orders.subtitle")}</p>

        {tooLate ? (
          /* Too late — show WhatsApp contact */
          <div className="rounded-xl border bg-card p-8 text-center space-y-4">
            <div className="mx-auto w-fit rounded-full bg-destructive/10 p-4">
              <Clock className="h-8 w-8 text-destructive" />
            </div>
            <p className="font-medium text-destructive">{t("orders.editTooLate")}</p>
            <p className="text-sm text-muted-foreground max-w-sm mx-auto">{t("orders.editTooLateMessage")}</p>
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-green-600 px-6 py-3 font-medium text-white hover:bg-green-700"
            >
              {t("orders.contactWhatsApp")}
            </a>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Current appointment info */}
            <div className="rounded-xl border bg-muted/30 p-4 space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t("orders.editCurrentAppointment")}</p>
              <div className="flex items-center gap-2 text-sm">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">{t("booking.dateTime")}:</span>
                <span className="font-medium">
                  {order.appointmentDate ? format(new Date(order.appointmentDate), "EEE, MMM d, yyyy") : ""}
                  {order.appointmentTime ? ` — ${order.appointmentTime}` : ""}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">{t("booking.address")}:</span>
                <span className="font-medium truncate">{order.customerAddress}</span>
              </div>
            </div>

            {/* Address section */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2 text-sm font-medium">
                <MapPin className="h-4 w-4" />
                {t("booking.address")}
              </Label>
              <div className="space-y-2">
                {/* Saved addresses (includes the current order's address if it's saved) */}
                {allAddresses.map((addr) => {
                    const isSelected = selectedAddressId === addr.id;
                    const extraFee = getAddressExtraFee(addr);
                    const govName = isRtl
                      ? addr.governorateNameAr || addr.governorate?.nameAr
                      : addr.governorateNameEn || addr.governorate?.nameEn;
                    const distName = isRtl
                      ? addr.districtNameAr || addr.district?.nameAr
                      : addr.districtNameEn || addr.district?.nameEn;
                    return (
                      <button
                        key={addr.id}
                        type="button"
                        onClick={() => setSelectedAddressId(isSelected ? "" : addr.id)}
                        className={cn(
                          "w-full text-start p-3 rounded-xl border-2 transition-all",
                          isSelected
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-primary/50 hover:bg-muted/50",
                        )}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium truncate">{addr.label}</span>
                          {isSelected ? (
                            <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                          ) : null}
                        </div>
                        {(govName || distName) && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {[govName, distName].filter(Boolean).join("، ")}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          {[
                            addr.block && `${t("addresses.block")} ${addr.block}`,
                            addr.street && `${t("addresses.street")} ${addr.street}`,
                            addr.avenue && `${t("addresses.avenue")} ${addr.avenue}`,
                            addr.building && `${t("addresses.building")} ${addr.building}`,
                          ].filter(Boolean).join("، ")}
                        </p>
                        {addr.landmark && (
                          <p className="text-xs text-muted-foreground/80 mt-0.5">{addr.landmark}</p>
                        )}
                        {extraFee > 0 && (
                          <p className="text-xs text-amber-600 dark:text-amber-500 mt-1.5 font-medium">
                            {t("orders.editAddressExtraFee", { fee: extraFee.toFixed(3) })}
                          </p>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Create new address link */}
                <Link href="/addresses" className="flex items-center justify-center gap-2 p-3 rounded-xl border-2 border-dashed border-border text-sm text-muted-foreground hover:border-primary/50 hover:text-foreground transition-all">
                  <Plus className="h-4 w-4" />
                  {t("orders.editCreateNewAddress")}
                </Link>
              </div>

            {/* Date picker — horizontal scroll, same as Booking */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">{t("booking.preferredDate")}</Label>
              <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
                {dateOptions.map((date, index) => {
                  const isSelected = selectedDate && format(date, "yyyy-MM-dd") === format(selectedDate, "yyyy-MM-dd");
                  const isToday = format(date, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd");
                  const dayName = isToday ? t("booking.today") : getDayName(date);
                  const dayNumber = format(date, "d");
                  return (
                    <button
                      key={index}
                      type="button"
                      onClick={() => {
                        setSelectedDate(date);
                        setSelectedTimeSlotId("");
                      }}
                      className={cn(
                        "flex-shrink-0 flex flex-col items-center justify-center p-3 rounded-xl border-2 min-w-[70px] transition-all",
                        isSelected
                          ? "border-primary bg-primary/10 text-white"
                          : "border-border hover:border-primary/50 hover:bg-muted/50",
                      )}
                    >
                      <span className="text-xs font-medium">{dayName}</span>
                      <span className="text-lg font-semibold">{dayNumber}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Time slots — grid, same as Booking */}
            {selectedDate && (
              <div className="space-y-3">
                <Label className="text-sm font-medium">{t("booking.preferredTime")}</Label>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {slotsLoading ? (
                    <>
                      {[1, 2, 3, 4, 5].map((i) => (
                        <div
                          key={i}
                          className="flex flex-col items-center justify-center p-3 rounded-xl border-2 animate-pulse bg-muted"
                        >
                          <div className="h-4 w-12 bg-muted-foreground/20 rounded mb-1"></div>
                          <div className="h-3 w-10 bg-muted-foreground/20 rounded"></div>
                        </div>
                      ))}
                    </>
                  ) : (timeSlots || []).length === 0 ? (
                    <div className="col-span-full text-center text-sm text-muted-foreground py-4">
                      {t("booking.noTimeSlots")}
                    </div>
                  ) : (
                    (timeSlots || []).map((slot: any) => {
                      const isSelected = selectedTimeSlotId === slot.id;
                      return (
                        <button
                          key={slot.id}
                          type="button"
                          onClick={() => setSelectedTimeSlotId(slot.id)}
                          className={cn(
                            "flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all",
                            isSelected
                              ? "border-primary bg-primary/10 text-white"
                              : "border-border hover:border-primary/50 hover:bg-muted/50",
                          )}
                        >
                          <span className="text-sm font-medium">{slot.time}</span>
                          {slot.isLateNight && (
                            <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-indigo-500/15 text-indigo-600 dark:text-indigo-400">
                              {t("booking.lateNight")}
                            </span>
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            )}

            {editMutation.isError && (
              <p className="text-sm text-destructive">
                {editMutation.error?.message || t("orders.editFailed")}
              </p>
            )}

            {editMutation.isSuccess && (
              <p className="text-sm text-green-600 flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" />
                {t("orders.editSuccess")}
              </p>
            )}

            {/* Payment method for the delivery-fee difference (only when the
                selected address is in a higher-fee area). */}
            {selectedExtraFee > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  {t("orders.editPaymentMethod")}
                </Label>
                <p className="text-xs text-muted-foreground">
                  {t("orders.editAddressExtraFee", { fee: selectedExtraFee.toFixed(3) })}
                </p>
                {/* Wallet option (disabled if balance can't cover the difference) */}
                <button
                  type="button"
                  onClick={() => walletBalance >= selectedExtraFee && setPaymentMethod('wallet')}
                  disabled={walletBalance < selectedExtraFee}
                  className={cn(
                    "w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all",
                    paymentMethod === 'wallet'
                      ? "border-primary bg-primary/5"
                      : walletBalance >= selectedExtraFee
                        ? "border-border hover:border-primary/50"
                        : "border-muted opacity-60 cursor-not-allowed",
                  )}
                >
                  <Wallet className="h-6 w-6 text-primary shrink-0" />
                  <div className="flex-1 text-start">
                    <p className="text-sm font-medium">{t("orders.wallet")}</p>
                    <p className="text-xs text-muted-foreground">
                      {t("orders.walletBalance")}: {walletBalance.toFixed(3)} {t("services.currency")}
                    </p>
                  </div>
                  {walletBalance < selectedExtraFee && (
                    <span className="text-xs text-destructive">{t("orders.insufficientBalance")}</span>
                  )}
                </button>
                {/* Gateway options */}
                <div className={cn("grid gap-3", "grid-cols-3")}>
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('knet')}
                    className={cn(
                      "flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all",
                      paymentMethod === 'knet' ? "border-primary bg-primary/5" : "border-border hover:border-primary/50",
                    )}
                  >
                    <img src={knetLogo} alt="KNET" className="w-10 h-10 object-contain rounded-lg" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('credit_card')}
                    className={cn(
                      "flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all",
                      paymentMethod === 'credit_card' ? "border-primary bg-primary/5" : "border-border hover:border-primary/50",
                    )}
                  >
                    <img src={creditCardLogos} alt="Visa / Mastercard" className="w-20 h-10 object-contain rounded-lg" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('apple_pay')}
                    className={cn(
                      "flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all",
                      paymentMethod === 'apple_pay' ? "border-primary bg-primary/5" : "border-border hover:border-primary/50",
                    )}
                  >
                    <img src={theme === 'dark' ? applePayLogoDark : applePayLogoLight} alt="Apple Pay" className="h-10 object-contain" />
                  </button>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <Link href="/orders" className="flex-1">
                <Button variant="outline" className="w-full" disabled={editMutation.isPending}>
                  {t("orders.cancelEdit")}
                </Button>
              </Link>
              <Button
                className="flex-1"
                onClick={handleSave}
                disabled={editMutation.isPending || (!selectedAddressId && !selectedTimeSlotId)}
              >
                {editMutation.isPending && <Loader2 className="h-4 w-4 me-2 animate-spin" />}
                {t("common.save")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
